import { Merge, Nullable, SimpleAnyObject } from '@clearverify/types';

import { NullsOrder, QueryMode, SortOrder, SortOrderInput } from 'src/api';
import { SortOption } from 'src/types/sort';

type ConnectToEntity = {
  [entityKey: string]: { connect: { id: string } };
};

type ConnectedEntity<TData> = Merge<TData, ConnectToEntity>;
type ConnectResult<TData> = {
  to: (entityKey: string, entityId: string) => ConnectedEntity<TData>;
};

const PREDICATE_WRAPPERS = ['AND', 'OR', 'NOT'] as const;
type GqlHavingWrappers = (typeof PREDICATE_WRAPPERS)[number];

export function connect<TData extends Record<string, unknown>>(
  connectData: TData,
): ConnectResult<TData> {
  return {
    to(entityKey: string, entityId: string) {
      return {
        [entityKey]: {
          connect: {
            id: entityId,
          },
        },
        ...connectData,
      };
    },
  };
}

function wrapTo<T = unknown>(to: GqlHavingWrappers, data: T) {
  return {
    [to]: data,
  };
}

export const gql = {
  PREDICATE_WRAPPERS,
  connect,
  wrapToOR<Tuple extends Array<unknown>>(predicates: Tuple) {
    return wrapTo<Tuple>('OR', predicates);
  },
  wrapToAND<Tuple extends Array<unknown>>(predicates: Tuple) {
    return wrapTo<Tuple>('AND', predicates);
  },
  gte<T>(field: string, value: T) {
    return {
      [field]: {
        gte: value,
      },
    };
  },
  lte<T>(field: string, value: T) {
    return {
      [field]: {
        lte: value,
      },
    };
  },
  in<T>(field: string, values: T[]) {
    return {
      [field]: {
        in: values,
      },
    };
  },
  contains<T>(field: string, value: T) {
    return {
      [field]: {
        contains: value,
        mode: QueryMode.Insensitive,
      },
    };
  },
  equals<T>(field: string, value: T) {
    return {
      [field]: {
        equals: value,
      },
    };
  },
};

export const createSortObject = (colKey: string, sortOrder: Nullable<SortOrder>) => {
  const keys = colKey.split('.');
  const sortObject: SimpleAnyObject = {};
  let current = sortObject;

  for (let idx = 0; idx < keys.length; idx++) {
    const key = keys[idx];
    if (!key) continue;
    if (idx === keys.length - 1) {
      current[key] = sortOrder;
    } else {
      current[key] = {};
      current = current[key];
    }
  }

  return sortObject;
};

export const createOrderByQueryVariable = (
  sortOption: Nullable<SortOption>,
): SimpleAnyObject | SimpleAnyObject[] => {
  if (!sortOption || !sortOption.order) return [];

  const keys = sortOption.columnKey.split('.');
  const sortObject: SimpleAnyObject = {};
  let current = sortObject;

  for (let idx = 0; idx < keys.length; idx++) {
    const key = keys[idx];
    if (!key) continue;
    if (idx === keys.length - 1) {
      if (sortOption.nonNull) {
        current[key] = sortOption.order;
      } else {
        current[key] = { sort: sortOption.order, nulls: NullsOrder.Last } satisfies SortOrderInput;
      }
    } else {
      current[key] = {};
      current = current[key];
    }
  }

  return sortObject;
};
